import type { GalleryPreset } from "@profolio/templates";
import { Eye, Sparkles, Check, Layers, BookOpen, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";

interface TemplateCardProps {
  preset: GalleryPreset;
  onPreview: (preset: GalleryPreset) => void;
  onApply: (preset: GalleryPreset) => void;
  isCurrentlyApplied?: boolean;
}

export function TemplateCard({
  preset,
  onPreview,
  onApply,
  isCurrentlyApplied = false,
}: TemplateCardProps) {
  const getCategoryIcon = () => {
    switch (preset.category) {
      case "faculty-teaching":
        return <BookOpen className="h-3.5 w-3.5" />;
      case "research-publications":
        return <Layers className="h-3.5 w-3.5" />;
      case "early-career-phd":
        return <GraduationCap className="h-3.5 w-3.5" />;
      default:
        return <Sparkles className="h-3.5 w-3.5" />;
    }
  };

  const getTierBadge = () => {
    switch (preset.requiredTier) {
      case "premium":
        return (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20">
            PREMIUM
          </span>
        );
      case "pro":
        return (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
            PRO
          </span>
        );
      case "starter":
      default:
        return (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
            STARTER
          </span>
        );
    }
  };

  return (
    <div className="group relative flex flex-col justify-between rounded-2xl border border-border bg-card/80 backdrop-blur-xs p-5 shadow-xs hover:shadow-md hover:border-primary/40 transition-all duration-200">
      {/* ── Top Row: Category + Tier ── */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground capitalize">
          {getCategoryIcon()}
          <span>{preset.category.replace(/-/g, " ")}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {preset.isFeatured && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
              FEATURED
            </span>
          )}
          {getTierBadge()}
        </div>
      </div>

      {/* ── Visual Thumbnail / Mockup Area ── */}
      <div
        onClick={() => onPreview(preset)}
        className="relative w-full aspect-16/10 rounded-xl overflow-hidden border border-border/60 bg-muted/30 group-hover:border-primary/30 transition-all cursor-pointer mb-4 flex items-center justify-center select-none"
      >
        {/* Abstract Architectural Mockup representation */}
        <div className="absolute inset-0 bg-linear-to-br from-card via-muted/20 to-primary/5 p-4 flex flex-col justify-between opacity-90 group-hover:opacity-100 transition-opacity">
          {/* Header wireframe */}
          <div className="flex items-center justify-between w-full border-b border-border/40 pb-2">
            <div className="h-2.5 w-16 bg-foreground/20 rounded-xs" />
            <div className="flex gap-1.5">
              <div className="h-2 w-6 bg-muted-foreground/30 rounded-xs" />
              <div className="h-2 w-6 bg-muted-foreground/30 rounded-xs" />
              <div className="h-2 w-6 bg-muted-foreground/30 rounded-xs" />
            </div>
          </div>
          {/* Hero wireframe */}
          <div className="flex gap-3 items-center my-auto">
            <div className="h-10 w-10 rounded-full bg-primary/20 shrink-0 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary">
              {preset.name.charAt(0)}
            </div>
            <div className="flex flex-col gap-1.5 w-full">
              <div className="h-3 w-3/4 bg-foreground/30 rounded-xs" />
              <div className="h-2 w-1/2 bg-muted-foreground/20 rounded-xs" />
            </div>
          </div>
          {/* Cards wireframe */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/40">
            <div className="h-6 rounded bg-card/60 border border-border/40" />
            <div className="h-6 rounded bg-card/60 border border-border/40" />
          </div>
        </div>

        {/* Hover Overlay with Preview button */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <span className="flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg bg-card text-foreground shadow-lg">
            <Eye className="h-3.5 w-3.5 text-primary" />
            Interactive Preview
          </span>
        </div>
      </div>

      {/* ── Text Details ── */}
      <div className="flex-1 flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors">
              {preset.name}
            </h3>
            {isCurrentlyApplied && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                <Check className="h-3.5 w-3.5" />
                Active
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-2">
            {preset.tagline}
          </p>
        </div>

        {/* Emphasis Pills */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {preset.filters.contentEmphasis.slice(0, 3).map((item) => (
            <span
              key={item}
              className="text-[10px] font-medium px-2 py-0.5 rounded-md bg-muted text-muted-foreground capitalize"
            >
              {item}
            </span>
          ))}
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-md bg-muted/60 text-muted-foreground capitalize">
            {preset.filters.aesthetic}
          </span>
        </div>

        {/* ── Actions ── */}
        <div className="grid grid-cols-2 gap-2 pt-3 border-t border-border/60">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPreview(preset)}
            className="w-full text-xs font-medium cursor-pointer"
          >
            <Eye className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
            Preview
          </Button>

          <Button
            type="button"
            size="sm"
            onClick={() => onApply(preset)}
            className="w-full text-xs font-semibold cursor-pointer"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1" />
            Use Template
          </Button>
        </div>
      </div>
    </div>
  );
}
